import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { FileCheck2, Loader2, RefreshCw } from 'lucide-react';
import type { RootState } from '../store/store';
import useAuthStore from '../store/authStore';
import { API_CONFIG } from '../config/api.config';
import { prepareCommonHeaders } from '../services/baseApi';
import {
  EMPTY_MANUAL_FIELDS,
  generateAndDownload,
  type ManualFields,
  type ValidationData,
} from '../utils/validationDocs';

// Same convention used by Sidebar.tsx / SuperAdminPage.tsx — no shared hook exists yet.
const SUPER_ADMIN_EMAILS = ['adam@thermal-systems.co.uk', 'dave@thermal-systems.co.uk'];

type CustomerRow = { id: number; name: string; mqtt_topic?: string };

function isoDateDaysAgo(days: number): string {
  return new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

const MANUAL_FIELD_GROUPS: Array<{
  title: string;
  fields: Array<{ key: keyof ManualFields; label: string; multiline?: boolean }>;
}> = [
  {
    title: 'Site / Installation (IQ)',
    fields: [
      { key: 'siteAddress', label: 'Site address' },
      { key: 'installLocationNotes', label: 'Physical install location notes', multiline: true },
    ],
  },
  {
    title: 'Operational test (OQ)',
    fields: [
      { key: 'calibrationCertRef', label: 'Calibration certificate reference(s)' },
      { key: 'testDate', label: 'Functional test date' },
      { key: 'testResult', label: 'Functional test result' },
      { key: 'testNotes', label: 'Functional test notes', multiline: true },
    ],
  },
  {
    title: 'Performance (PQ)',
    fields: [
      { key: 'ambientConditions', label: 'Ambient / site conditions', multiline: true },
      { key: 'acceptanceCriteria', label: 'Acceptance criteria' },
      { key: 'pqPassFail', label: 'Pass / fail' },
      { key: 'pqNotes', label: 'PQ notes', multiline: true },
    ],
  },
  {
    title: 'Sign-off',
    fields: [
      { key: 'preparedBy', label: 'Prepared by' },
      { key: 'verifiedBy', label: 'Verified by' },
    ],
  },
];

export default function ValidationDocsPage() {
  const authUser = useSelector((state: RootState) => state.auth.user);
  const zustandUser = useAuthStore((state) => state.user);
  const activeEmail = (authUser?.email || zustandUser?.email || '').toLowerCase();
  const isSuperAdmin = SUPER_ADMIN_EMAILS.map((e) => e.toLowerCase()).includes(activeEmail);

  const [customers, setCustomers] = useState<CustomerRow[] | null>(null);
  const [customersError, setCustomersError] = useState<string | null>(null);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [from, setFrom] = useState(isoDateDaysAgo(7));
  const [to, setTo] = useState(isoDateDaysAgo(0));

  const [manual, setManual] = useState<ManualFields>(EMPTY_MANUAL_FIELDS);

  const [data, setData] = useState<ValidationData | null>(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [dataError, setDataError] = useState<string | null>(null);
  const [generating, setGenerating] = useState<'IQ' | 'OQ' | 'PQ' | null>(null);

  useEffect(() => {
    if (!isSuperAdmin) return;
    (async () => {
      try {
        const res = await fetch(`${API_CONFIG.baseURL}/super-admin/customers`, {
          method: 'GET',
          credentials: 'include',
          headers: prepareCommonHeaders(),
        });
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          setCustomersError(j.error || `HTTP ${res.status}`);
          return;
        }
        setCustomers(await res.json());
      } catch (e) {
        setCustomersError(e instanceof Error ? e.message : 'Request failed');
      }
    })();
  }, [isSuperAdmin]);

  const loadData = async () => {
    if (!selectedCompany) return;
    setDataLoading(true);
    setDataError(null);
    setData(null);
    try {
      const params = new URLSearchParams({
        company: selectedCompany,
        from: new Date(from).toISOString(),
        to: new Date(to).toISOString(),
      });
      const res = await fetch(`${API_CONFIG.baseURL}/super-admin/validation-data?${params}`, {
        method: 'GET',
        credentials: 'include',
        headers: prepareCommonHeaders(),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        setDataError(j.error || `HTTP ${res.status}`);
        return;
      }
      setData(await res.json());
    } catch (e) {
      setDataError(e instanceof Error ? e.message : 'Request failed');
    } finally {
      setDataLoading(false);
    }
  };

  const handleGenerate = async (docType: 'IQ' | 'OQ' | 'PQ') => {
    if (!data) return;
    setGenerating(docType);
    try {
      await generateAndDownload(docType, data, manual);
    } finally {
      setGenerating(null);
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="min-h-screen bg-app-canvas dark:bg-gray-900 min-w-0 flex items-center justify-center">
        <p className="text-gray-500 dark:text-gray-400">This page isn&apos;t available on your account.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-app-canvas dark:bg-gray-900 min-w-0">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-5 md:py-8 min-w-0 max-w-full">
        <div className="mb-5 md:mb-7">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <FileCheck2 className="h-7 w-7" aria-hidden="true" />
            Validation Documents
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Generate IQ / OQ / PQ validation documents (.docx) for a customer, pulling evidence directly from
            their system data. Super-admin only — not visible to customers.
          </p>
        </div>

        <div className="bg-gray-50 dark:bg-gray-800 shadow rounded-lg p-4 sm:p-6 mb-6 space-y-4">
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Customer</label>
              <select
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm px-3 py-2"
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
              >
                <option value="">Select a customer…</option>
                {customers?.map((c) => (
                  <option key={c.id} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
              {customersError && <p className="mt-1 text-xs text-red-500">{customersError}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                PQ period start
              </label>
              <input
                type="date"
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm px-3 py-2"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                PQ period end
              </label>
              <input
                type="date"
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm px-3 py-2"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </div>
          </div>

          <button
            onClick={loadData}
            disabled={!selectedCompany || dataLoading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium bg-[var(--brand-color)] text-white disabled:opacity-40"
          >
            {dataLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Load system data
          </button>
          {dataError && <p className="text-xs text-red-500">{dataError}</p>}
          {data && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Loaded {data.sensors.length} sensors, {data.zones.length} zones, {data.users.length} users,{' '}
              {data.auditLog.length} audit entries, {data.alarmLog.length} alarm entries for {data.company}.
            </p>
          )}
        </div>

        <div className="bg-gray-50 dark:bg-gray-800 shadow rounded-lg p-4 sm:p-6 mb-6 space-y-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Manual fields <span className="text-xs font-normal text-gray-400">(not tracked by the system)</span>
          </h2>
          {MANUAL_FIELD_GROUPS.map((group) => (
            <div key={group.title}>
              <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-300 mb-2">{group.title}</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {group.fields.map((f) => (
                  <div key={f.key} className={f.multiline ? 'sm:col-span-2' : ''}>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      {f.label}
                    </label>
                    {f.multiline ? (
                      <textarea
                        className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm px-3 py-2"
                        rows={2}
                        value={manual[f.key]}
                        onChange={(e) => setManual((m) => ({ ...m, [f.key]: e.target.value }))}
                      />
                    ) : (
                      <input
                        type="text"
                        className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm px-3 py-2"
                        value={manual[f.key]}
                        onChange={(e) => setManual((m) => ({ ...m, [f.key]: e.target.value }))}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="bg-gray-50 dark:bg-gray-800 shadow rounded-lg p-4 sm:p-6 flex flex-wrap gap-3">
          {(['IQ', 'OQ', 'PQ'] as const).map((docType) => (
            <button
              key={docType}
              onClick={() => handleGenerate(docType)}
              disabled={!data || generating !== null}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium border border-[var(--brand-color)] text-[var(--brand-color)] disabled:opacity-40"
            >
              {generating === docType ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileCheck2 className="h-4 w-4" />}
              Generate {docType}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
